import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Clock, Award, Sparkles, ArrowRight } from "lucide-react";

// Role-coded catalog. Each course lists the roles it is coded for; the Academy
// only shows courses matching the viewer's role.
const CATALOG = [
  {
    code: "FAC-TSB-CERT", name: "TSB Benchmark Certification (Instructor)", roles: ["advisor", "admin"],
    length: "5 modules · quizzes + certification exam · ~4 hrs", featured: true, to: "/app/advisor/certification",
    desc: "The comprehensive instructor certification in the Transferable Skills Benchmark: what it measures, the 4-level scale, domain roll-up, advising with the data, and keeping interests separate from skills. Pass the exam to earn a 3-year certificate.",
  },
  {
    code: "FAC-101", name: "Embedding Transferable Skills in Your Course", roles: ["advisor", "admin"],
    length: "3 modules · 2 hrs",
    desc: "Design assignments and rubrics that surface durable skills alongside content mastery.",
  },
  {
    code: "FAC-210", name: "Coaching Students on Their TSB", roles: ["advisor", "admin"],
    length: "3 modules · 2.5 hrs",
    desc: "Turn a benchmark into a growth conversation: strengths, focus areas, and next steps.",
  },
  {
    code: "FAC-220", name: "Interpreting the 4-Level Developmental Scale", roles: ["advisor", "admin"],
    length: "2 modules · 1.5 hrs",
    desc: "Read Emerging → Developing → Proficient → Advanced consistently and fairly across skills.",
  },
  {
    code: "ADV-330", name: "Advising Playbook: From Benchmark to Action", roles: ["advisor", "admin"],
    length: "3 modules · 2 hrs",
    desc: "Map low domains to Learning Academy modules and schedule effective check-ins.",
  },
  // Student-coded course intentionally hidden from the faculty view.
  { code: "LA-SM101", name: "Self-Management & Personal Productivity", roles: ["student"], length: "3 modules · 2 hrs", desc: "" },
];

export default function AdvisorAcademy() {
  const { user } = useAuth();
  const role = user?.role || "advisor";
  const courses = CATALOG.filter((c) => c.roles.includes(role));
  const featured = courses.find((c) => c.featured);
  const rest = courses.filter((c) => !c.featured);

  return (
    <DashboardShell
      title="Learning Academy"
      subtitle="Faculty & advisor professional development — courses coded for your role"
      nav={ADVISOR_NAV}
    >
      <Card className="mb-6 border-[#78be21]/40 bg-[#78be21]/5 p-4">
        <p className="flex items-center gap-2 text-sm text-slate-700">
          <Sparkles className="h-5 w-5 text-[#5fa114]" />
          You're seeing courses coded for <span className="font-semibold capitalize">{role === "admin" ? "faculty / admin" : "faculty"}</span> roles. Student micro-courses live in the student portal.
        </p>
      </Card>

      {featured && (
        <Card className="mb-8 overflow-hidden border-[#003057]/15 card-elevate" data-testid="academy-featured">
          <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#003057] text-white"><Award className="h-6 w-6" /></div>
              <div>
                <span className="rounded-full bg-[#78be21]/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#3d6b0e]">Certification</span>
                <p className="mt-1 font-heading text-lg font-bold text-[#003057]">{featured.name}</p>
                <p className="font-mono-num text-[11px] font-semibold uppercase tracking-wide text-slate-400">{featured.code} · {featured.length}</p>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{featured.desc}</p>
              </div>
            </div>
            <Link to={featured.to}>
              <Button className="rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid="academy-cert-cta">
                Open course <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="academy-course-grid">
        {rest.map((c) => (
          <div key={c.code} className="rounded-xl border border-slate-200 bg-white p-4 card-elevate" data-testid={`faculty-course-${c.code}`}>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#005291]/10">
                <BookOpen className="h-4.5 w-4.5" style={{ width: 18, height: 18, color: "#005291" }} />
              </div>
              <div>
                <p className="font-heading text-sm font-bold text-[#003057]">{c.name}</p>
                <p className="font-mono-num text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.code}</p>
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.desc}</p>
            <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-400"><Clock className="h-3.5 w-3.5" /> {c.length}</p>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
